// ---------------------------------
// client/src/components/Header.jsx
// Global header with logo & navigation
// ---------------------------------
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
import { fetchCurrentUser } from '../utils/users';
import { logoutRequest } from '../utils/api';
import { resolveAssetUrl } from '../utils/assets';

const TEXT = {
    home: 'בית',
    dashboard: 'עבודות',
    manual: 'מדריך משתמש',
    contact: 'צור קשר',
    adminUsers: 'משתמשים',
    adminJobTypes: 'סוגי עבודה',
    adminTemplates: 'תבניות PDF',
    login: 'כניסה',
    logout: 'יציאה',
    profileFallback: 'פרופיל',
};

function getInitials(name) {
    if (!name) return '—';
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
    return `${first}${last}` || '—';
}

export default function Header() {
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const isAuthenticated = Boolean(token);
    const [profile, setProfile] = useState(null);
    let isAdmin = false;

    if (token) {
        try {
            const { role } = jwt_decode(token);
            isAdmin = role === 'admin';
        } catch {
            // ignore invalid token
        }
    }

    const handleLogout = async () => {
        try {
            await logoutRequest();
        } catch {
            // ignore logout errors
        }
        localStorage.removeItem('token');
        setProfile(null);
        navigate('/login', { replace: true });
    };

    useEffect(() => {
        let isMounted = true;
        if (!isAuthenticated) {
            setProfile(null);
            return () => {
                isMounted = false;
            };
        }
        fetchCurrentUser()
            .then((data) => {
                if (isMounted) {
                    setProfile(data);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setProfile(null);
                }
            });
        return () => {
            isMounted = false;
        };
    }, [isAuthenticated]);

    const profileLabel = profile?.name || TEXT.profileFallback;
    const profileInitials = getInitials(profile?.name || '');
    const profileImageUrl = resolveAssetUrl(profile?.profileImageUrl);

    return (
        <header className="header">
            <Link to="/" className="header__logo" aria-label={'לאור הנדסה ואחזקה תעשייתית בע"מ'}>
                <img src="/logo.jpg" alt={'לוגו לאור הנדסה ואחזקה תעשייתית בע"מ'} className="logo" />
            </Link>
            <nav className="header__nav" aria-label="Primary">
                <ul>
                    <li><Link to="/">{TEXT.home}</Link></li>
                    {isAuthenticated && <li><Link to="/dashboard">{TEXT.dashboard}</Link></li>}
                    {isAuthenticated && <li><Link to="/instructions-manual">{TEXT.manual}</Link></li>}
                    {isAuthenticated && <li><Link to="/contact-us">{TEXT.contact}</Link></li>}
                    {isAdmin && (
                        <>
                            <li><Link to="/admin/debug">דיבאג</Link></li>
                            <li><Link to="/admin/users">{TEXT.adminUsers}</Link></li>
                            <li><Link to="/admin/job-types">{TEXT.adminJobTypes}</Link></li>
                            <li><Link to="/admin/pdf-templates">{TEXT.adminTemplates}</Link></li>
                        </>
                    )}
                    {isAuthenticated && (
                        <li>
                            <Link to="/profile" className="header__profile" aria-label={profileLabel}>
                                {profileImageUrl ? (
                                    <img
                                        src={profileImageUrl}
                                        alt={profileLabel}
                                        className="header__avatar"
                                    />
                                ) : (
                                    <span className="header__avatar header__avatar--fallback">
                                        {profileInitials}
                                    </span>
                                )}
                                <span className="header__profile-name">{profileLabel}</span>
                            </Link>
                        </li>
                    )}
                    {!isAuthenticated ? (
                        <li><Link to="/login">{TEXT.login}</Link></li>
                    ) : (
                        <li>
                            <button type="button" className="link-button" onClick={handleLogout}>
                                {TEXT.logout}
                            </button>
                        </li>
                    )}
                </ul>
            </nav>
        </header>
    );
}
